import { useState } from "react";
import { useSelector } from "react-redux";
import {
  StyledGridRow,
  StyledGridCol,
  StyledSectionHeading,
} from "@/common/styles";
import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import CustomModal from "../CustomModal";
import ReportInformation from "./OfficerReports/ReportInformation";
import OffenseDetails from "./OfficerReports/OffenseDetails";
import OfficerReportLocationSection from "./OfficerReports/OfficerReportLocationSection";
import OfficerReportNarrative from "./OfficerReports/OfficerReportNarrative";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import SupplementalDetails from "./OfficerReports/SupplementalDetails";
import OfficerReportAdministrativeInfo from "./OfficerReports/OfficerReportAdministrativeInfo";
import { MODULES_LIST } from "@/util/common_variable";

const OfficerReportsDetails = () => {
  const bookings = useSelector((state) => state.bookings?.bookings);
  const officerReportsPayload = bookings?.jackets?.[0]?.officerReports || [];

  const [showOfficerReport, setShowOfficerReport] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const totalRecords = officerReportsPayload.length;
  const startIndex = pagination.pageIndex * pagination.pageSize;
  const paginatedReports = officerReportsPayload.slice(
    startIndex,
    startIndex + pagination.pageSize,
  );

  const tableRows = paginatedReports.map((report, index) => ({
    id: startIndex + index + 1,
    bookingNumber: report.bookingID || "-",
    occurredOn: report.dateOccurred,
    crnNo: report.crnNo,
    reportType: report.reportType,
    actionRequested: report.actionRequested || "-",
    // Save full report data for use on row click
    reportData: report,
  }));

  const officerReportTable = {
    headers: [
      { key: "id", text: "#" },
      { key: "bookingNumber", text: "Booking Number" },
      { key: "occurredOn", text: "Occurred On" },
      { key: "crnNo", text: "CRN No" },
      { key: "reportType", text: "Report Type" },
      { key: "actionRequested", text: "Action Requested" },
    ],
    data: tableRows,
  };

  const handleRowClick = (rowData) => {
    setSelectedReport(rowData.reportData);
  };

  const handleViewClick = () => {
    if (selectedReport) {
      setShowOfficerReport(true);
    }
  };

  return (
    <>
      <StyledGridRow justify={"end"}>
        <StyledGridCol>
          <Button onClick={handleViewClick} disabled={!selectedReport}>
            Details
          </Button>
        </StyledGridCol>
      </StyledGridRow>

      <Table
        selected={!selectedReport}
        headers={officerReportTable.headers}
        data={officerReportTable.data}
        style={{ marginTop: "15px" }}
        totalRecords={totalRecords}
        pagination={pagination}
        setPagination={setPagination}
        onRowClick={handleRowClick}
      />

      {showOfficerReport && selectedReport && (
        <CustomModal
          show={showOfficerReport}
          header="Report Details"
          size="xl"
          hideFooter={true}
          onHide={() => setShowOfficerReport(false)}
        >
          <StyledSectionHeading>
            <span>Report Information</span>
          </StyledSectionHeading>
          <ReportInformation report={selectedReport} />

          <StyledSectionHeading>
            <span>Offenses</span>
          </StyledSectionHeading>
          <OffenseDetails report={selectedReport} />

          <StyledSectionHeading>
            <span>Location</span>
          </StyledSectionHeading>
          <OfficerReportLocationSection report={selectedReport} />

          <StyledSectionHeading>
            <span>Narrative</span>
          </StyledSectionHeading>
          <OfficerReportNarrative report={selectedReport} />

          <StyledSectionHeading>
            <span>Inmate Accept Consequences</span>
          </StyledSectionHeading>
          <FormItem
            label="Accept?"
            labelPostion="label-right"
            layout="horizontal"
            justify={"start"}
          >
            <Checkbox checked={selectedReport.accept} />
          </FormItem>

          <StyledSectionHeading marginTop="20px">
            <span>Hearing Information</span>
          </StyledSectionHeading>
          <FormItem
            label="Hearing?"
            labelPostion="label-right"
            layout="horizontal"
            justify={"start"}
          >
            <Checkbox checked={selectedReport.hearing} />
          </FormItem>

          <StyledSectionHeading marginTop="20px">
            <span>Appeal Information</span>
          </StyledSectionHeading>
          <FormItem
            label="Appeal?"
            labelPostion="label-right"
            layout="horizontal"
            justify={"start"}
          >
            <Checkbox checked={selectedReport.appeal} />
          </FormItem>

          <StyledSectionHeading marginTop="20px">
            <span>Approval Information</span>
          </StyledSectionHeading>
          <FormItem
            label="Approval?"
            labelPostion="label-right"
            layout="horizontal"
            justify={"start"}
          >
            <Checkbox checked={selectedReport.approved} />
          </FormItem>

          <StyledSectionHeading marginTop="20px">
            <span>Supplementals</span>
          </StyledSectionHeading>
          <SupplementalDetails
            report={selectedReport}
            from={MODULES_LIST.BOOKINGS}
          />

          <StyledSectionHeading marginTop="20px">
            <span>Officer Report Administrative Information</span>
          </StyledSectionHeading>
          <OfficerReportAdministrativeInfo report={selectedReport} />
        </CustomModal>
      )}
    </>
  );
};

export default OfficerReportsDetails;
